// CustomSelect.jsx
import React, {
  useId,
  useRef,
  useState,
  useEffect,
  forwardRef,
} from "react";
import "./CustomSelect.css";

const CustomSelect = forwardRef(function CustomSelect(
  {
    label,
    value,
    options,
    onChange,
    disabled,
    placeholder = "Select…",
    maxHeight = 280,
    menuWidth = 260
  },
  externalRef
) {
  const buttonRef = useRef(null);
  const listRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(null);
  const id = useId();

  const current = options.find((o) => o.value === value);

  const close = () => setOpen(false);

  useEffect(() => {
    function onDocClick(e) {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(e.target) &&
        listRef.current &&
        !listRef.current.contains(e.target)
      ) {
        close();
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  function toggle() {
    if (!disabled) setOpen((o) => !o);
  }

  function selectVal(v) {
    onChange?.(v);
    close();
    buttonRef.current?.focus();
  }

  function onKeyDown(e) {
    if (
      !open &&
      (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ")
    ) {
      e.preventDefault();
      setOpen(true);
      setHighlight(options.findIndex((o) => o.value === value) ?? 0);
      return;
    }
    if (!open) return;

    if (e.key === "Escape") {
      e.preventDefault();
      close();
      return;
    }

    const last = options.length - 1;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => (h === null ? 0 : Math.min(last, h + 1)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => (h === null ? 0 : Math.max(0, h - 1)));
    } else if (e.key === "Home") {
      e.preventDefault();
      setHighlight(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setHighlight(last);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const opt = options[highlight ?? 0];
      if (opt) selectVal(opt.value);
    }
  }

  return (
    <div className="cs-root">
      {label && (
        <label className="cs-label" id={`${id}-label`}>
          {label}
        </label>
      )}

      <button
        ref={(el) => {
          buttonRef.current = el;
          if (externalRef) externalRef.current = el;
        }}
        type="button"
        className="cs-button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={`${id}-label ${id}-button`}
        id={`${id}-button`}
        onClick={toggle}
        onKeyDown={onKeyDown}
        disabled={disabled}
      >
        <span className={`cs-value ${!current ? "cs-placeholder" : ""}`}>
          {current ? current.label : placeholder}
        </span>
        <span className="cs-caret" aria-hidden>
          ▾
        </span>
      </button>

      {open && (
        <ul
          ref={listRef}
          role="listbox"
          tabIndex={-1}
          aria-labelledby={`${id}-label`}
          className="cs-list"
          style={{ maxHeight, width: menuWidth }}
          onKeyDown={onKeyDown}
        >
          {options.map((opt, i) => {
            const isSelected = opt.value === value;
            const isActive = i === highlight;
            return (
              <li
                key={opt.value}
                role="option"
                aria-selected={isSelected}
                className={`cs-option ${
                  isSelected ? "is-selected" : ""
                } ${isActive ? "is-active" : ""}`}
                onMouseEnter={() => setHighlight(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => selectVal(opt.value)}
              >
                {opt.label}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
});

export default CustomSelect;